/**
 * Merge variables in the marketing builder: grouping the server's list, and
 * putting a `{{ key }}` token where the author's caret is.
 *
 * The server renders `{{ key }}` and `{{ key | fallback }}` (contract,
 * `docs/api/marketing.md`). A CUSTOM variable is addressed as
 * `custom.<key>` — the bare key would render as unresolved.
 *
 * ⚠️ NOTHING HERE VALIDATES A KEY. The list comes from the server and is
 * inserted verbatim; an author who types a token by hand gets it rendered or
 * reported as `unresolved` by the preview endpoint, which is the one place that
 * knows.
 */
import type { Editor } from 'grapesjs';

/** The fields of a built-in variable this module reads. */
export interface BuiltInVariableLike {
  key: string;
  label: string;
  group: string;
  example?: string | null;
}

/** The fields of a custom variable this module reads. */
export interface CustomVariableLike {
  key: string;
  label: string;
  value?: string | null;
}

export interface VariableItem {
  /** The token key, exactly as the renderer resolves it. */
  key: string;
  label: string;
  example: string | null;
}

export interface VariableGroup {
  label: string;
  items: VariableItem[];
}

/**
 * Built-ins grouped by the server's own `group`, in first-seen order, then the
 * custom ones under one group. Order is the server's — re-sorting here would
 * put two lists the Variables tab and this panel both show in two orders.
 */
export function groupVariables(
  builtIn: readonly BuiltInVariableLike[],
  custom: readonly CustomVariableLike[],
): VariableGroup[] {
  const groups = new Map<string, VariableItem[]>();
  for (const v of builtIn) {
    const label = v.group.trim() || 'Other';
    const items = groups.get(label) ?? [];
    items.push({ key: v.key, label: v.label, example: v.example ?? null });
    groups.set(label, items);
  }
  const result: VariableGroup[] = [...groups].map(([label, items]) => ({ label, items }));
  if (custom.length > 0) {
    result.push({
      label: 'Custom',
      items: custom.map((v) => ({
        key: `custom.${v.key}`,
        label: v.label,
        example: v.value ?? null,
      })),
    });
  }
  return result;
}

export const tokenFor = (key: string): string => `{{${key}}}`;

export type InsertOutcome = 'inserted' | 'copied' | 'failed';

/**
 * Into the text component being edited, at the caret; otherwise onto the
 * clipboard so the author can paste it wherever they meant.
 *
 * ⚠️ `insertHTML` with a plain string: the token contains no markup, and the
 * RTE's own method is used rather than `execCommand('insertText')`, which
 * browsers implement differently inside an iframe.
 */
export async function insertVariable(editor: Editor | null, key: string): Promise<InsertOutcome> {
  const token = tokenFor(key);
  const rte = editor?.RichTextEditor.globalRte;
  if (editor && editor.getEditing() && rte) {
    try {
      rte.insertHTML(token);
      return 'inserted';
    } catch {
      // Fall through to the clipboard — a lost caret is not worth a failure.
    }
  }
  try {
    await navigator.clipboard.writeText(token);
    return 'copied';
  } catch {
    return 'failed';
  }
}

const ACTION_NAME = 'emerge-insert-variable';

export interface VariableRteAction {
  /** Repopulates the dropdown — the list arrives after the editor exists. */
  setGroups(groups: readonly VariableGroup[]): void;
}

/**
 * Adds an "Insert variable" dropdown to the rich-text toolbar.
 *
 * ⚠️ THE SELECT IS ONE DOM ELEMENT HELD HERE AND REPOPULATED, not an icon
 * string: GrapesJS copies a string icon into the toolbar once, at the moment
 * the action is added, which is before the variables have been fetched. An
 * element is appended as-is, so filling it later reaches the live toolbar.
 *
 * The toolbar binds `on<event>` to the action's wrapper span; a select's
 * `change` bubbles to it, which is what makes `event: 'change'` work here.
 */
export function registerVariableRteAction(editor: Editor): VariableRteAction {
  const select = document.createElement('select');
  select.className = 'emerge-rte-variable';
  select.setAttribute('aria-label', 'Insert variable');
  select.title = 'Insert variable';

  const fill = (groups: readonly VariableGroup[]) => {
    select.replaceChildren();
    const first = document.createElement('option');
    first.value = '';
    first.textContent = groups.length > 0 ? 'Insert variable' : 'No variables';
    select.appendChild(first);
    for (const group of groups) {
      const optgroup = document.createElement('optgroup');
      optgroup.label = group.label;
      for (const item of group.items) {
        const option = document.createElement('option');
        option.value = item.key;
        option.textContent = item.label;
        optgroup.appendChild(option);
      }
      select.appendChild(optgroup);
    }
    select.value = '';
  };
  fill([]);

  editor.RichTextEditor.add(ACTION_NAME, {
    icon: select,
    event: 'change',
    attributes: { title: 'Insert variable' },
    result: (rte) => {
      const key = select.value;
      if (!key) return;
      rte.insertHTML(tokenFor(key));
      select.value = '';
    },
  });

  return { setGroups: fill };
}
